/**
 * @module cart-drawer
 * @description Cart drawer entry point — slide-in cart UI loaded on every page.
 *
 * Extends BaseDrawer to render cart contents with morph-based updates.
 * Listens to CART_EVENTS for real-time state sync across tabs/components.
 *
 * Public API: dispatches cart-drawer:open / cart-drawer:close CustomEvents.
 *
 * @see {@link @stardust/cart} — core cart state + Shopify API
 * @see {@link @stardust/base-drawer} — drawer lifecycle base class
 * @see {@link @stardust/cart-drawer-addons} — the add-on recommendations section
 */
import { morph, MORPH_OPTIONS } from "@stardust/morph";
import Cart, { CART_EVENTS } from "@stardust/cart";
import BaseDrawer from "@stardust/base-drawer";
import { formatMoney } from "@stardust/utilities";
/**
 * The add-on renderer is imported dynamically, never statically: it pulls add-to-cart and
 * product-addon-row and their own dependencies behind it, and nothing it does can happen before
 * the shopper adds something. The sessionStorage key below is duplicated rather than imported for
 * the same reason: the probe that decides whether to load the renderer cannot itself require
 * loading it.
 */
const ADDON_MODULE = "@stardust/cart-drawer-addons";

/**
 * Mirror of the key in cart-drawer-addons.js (asserted in tools/cart-drawer-addons.test.ts).
 * Duplicated deliberately: reading it here is a bare existence probe that lets this module skip
 * loading the renderer on the overwhelming majority of page views. Importing the key would
 * mean importing the module the probe exists to avoid.
 */
const ADDON_CTX_SESSION_KEY = "stardust:cart-drawer-addon-ctx";

/**
 * Whether a stored context could apply *here*. The path check matters: a context stored on another
 * page would otherwise pull in the renderer and its whole dependency tree only for it to be
 * discarded, on every page the shopper visits after adding something. The renderer stays the
 * authority — this is a pre-filter, and parsing the stored JSON is far cheaper than the modules it
 * avoids fetching.
 * @returns {boolean}
 */
function hasRestorableAddonContext() {
  try {
    const raw = sessionStorage.getItem(ADDON_CTX_SESSION_KEY);
    return Boolean(raw) && JSON.parse(raw).path === window.location.pathname;
  } catch {
    return false;
  }
}

/**
 * Local copy of the renderer's predicate — one line, and needed before the renderer is loaded.
 * @param {object|null|undefined} ctx
 */
function addonContextRenderable(ctx) {
  return Array.isArray(ctx?.products) && ctx.products.length > 0;
}

const CART_DRAWER_EVENTS = {
  open: "cart-drawer:open",
  close: "cart-drawer:close",
  /**
   * "Here are rows to render." Fired by <product-addons> (via add-to-cart.js), which owns computing
   * them. The drawer never asks and never computes — it listens, caches the payload, and renders.
   */
  addons: "cart-drawer:addons",
};

const CHECKOUT_SLOT_SEL = "[data-cart-drawer-checkout]";
const ITEMS_CONTAINER_SEL = "[cart-items-container]";
const CART_ITEMS_SEL = "[data-cart-items]";
const SCROLL_AREA_SEL = ".cart-drawer__content";
/** How long a pulse on an already-present row lasts before it resets. */
const FEEDBACK_RESET_MS = 2000;

/**
 * Cart Drawer — slide-in cart UI with morph-based updates.
 * Features: sticky summary, installments close, lazy hydration,
 * optimistic items, addon recommendations.
 */
class CartDrawer extends BaseDrawer {
  /** @type {number} If summary/drawer height ratio exceeds this, summary becomes static */
  _summaryThreshold = 0.5;

  constructor(element) {
    super(element, {
      openClass: "opacity-0 translate-x-full",
      closeClass: "opacity-0 translate-x-full",
    });
    this.cart = null;
    /** @type {import("@stardust/cart-drawer-addons").default|null} Set once the module loads. */
    this._addons = null;
    this._addonsPromise = null;
    /** Last renderable context, kept here so it survives without the renderer loaded. */
    this._pendingAddonContext = null;
    this._refreshGen = 0;
    this._addInFlight = false;
  }

  // ── Lifecycle ────────────────────────────────────────────────────

  load() {
    this._isLazy = this.element.hasAttribute('data-lazy-cart-drawer');
    this._hydrated = !this._isLazy;
    this._hydratePromise = null;

    this._fetchInitialCartCount();
    this._bindEvents();

    if (!this._isLazy) {
      this._initCartInstance();
    } else {
      // Hydrate eagerly in the background so content is ready when the user
      // clicks open (avoids a 1-2s blank-shell wait on first open).
      this._hydrateDrawer();
    }
  }

  /**
   * Bind all document-level event listeners.
   */
  _bindEvents() {
    document.addEventListener(CART_DRAWER_EVENTS.open, (e) => this._onOpenEvent(e));
    document.addEventListener(CART_DRAWER_EVENTS.close, this._boundClose);
    document.addEventListener(CART_EVENTS.updated, (e) => this._onCartUpdated(e.detail));
    document.addEventListener(CART_EVENTS.addStart, (e) => this._onAddStart(e.detail));
    document.addEventListener(CART_EVENTS.refreshDrawer, (e) => this._refreshDrawerHTML(e.detail));
    document.addEventListener(CART_EVENTS.addFailed, (e) => this._onAddFailed(e.detail));
    document.addEventListener(CART_DRAWER_EVENTS.addons, (e) => void this._renderAddons(e.detail));

  }

  _onOpenEvent(e) {
    if (this.isOpen) {
      // Already open and this is an add — the add-ons event handles rendering, nothing to do here.
      if (e.detail?.fromAddToCart) return;
      this.close();
      return;
    }

    if (!this._hydrated) this._hydrateDrawer();
    this._openWithContext(e);
  }

  _onCartUpdated(cartData) {
    if (cartData && this.cart) this.cart.cart = cartData;
    this._updateCartUI(cartData);

    if (this.isOpen && cartData?.item_count === 0 && !this._addInFlight) {
      this.close();
      return;
    }

    void this._restoreAddons(cartData);

    requestAnimationFrame(() => this._updateStickyState());
  }

  _onAddStart(detail) {
    this._addInFlight = true;
    // Lock quantity/remove controls while the add is in flight — a
    // cart/change.js sent mid-add can land after the add re-splits lines
    // and hit the wrong index. Unlocked by the refresh finally or _onAddFailed.
    this.cart?._setAllItemsLoading(true);
    const { variantId, optimisticData } = detail ?? {};
    if (variantId == null) return;

    if (optimisticData) {
      this._showOptimisticItem(optimisticData);
    } else {
      this._showSkeleton(variantId);
    }
  }

  _onAddFailed(detail) {
    this._addInFlight = false;
    this.cart?._setAllItemsLoading(false);
    this._removeOptimisticItem(detail?.variantId);
  }

  /**
   * Open the drawer with context from the dispatched event.
   * @param {CustomEvent} e
   */
  _openWithContext(e) {
    this.open();

    // The region ships empty — the server used to bake in "Cart drawer opened.", but it sat inside
    // the morph target, so every refresh re-announced it. Writing it here means it fires on open
    // and only on open.
    const liveEl = this.element.querySelector("[data-cart-drawer-aria-live-text]");
    if (liveEl) liveEl.textContent = e.detail?.ariaLiveText || "Cart drawer opened";

    // Replay the last rows we were handed, so re-opening the drawer keeps showing them.
    void this._renderAddons(this._pendingAddonContext);

    requestAnimationFrame(() => {
      this._updateStickyState();
      this._setupInstallmentsClose();
    });
  }

  /** BaseDrawer lifecycle hook — hide addons when drawer closes. */
  onClose() {
    // Nothing to hide if the renderer never loaded.
    this._addons?.hide();
  }

  // ── Add-ons (lazily loaded) ──────────────────────────────────────

  /**
   * Import and instantiate the add-on renderer once, on first real need.
   * @returns {Promise<object|null>} null when the module can't be loaded.
   */
  async _loadAddons() {
    if (this._addons) return this._addons;
    if (!this._addonsPromise) {
      this._addonsPromise = import(ADDON_MODULE)
        .then(({ default: CartDrawerAddons }) => {
          this._addons = new CartDrawerAddons(this.element);
          return this._addons;
        })
        .catch((error) => {
          // Add-ons are an enhancement — the drawer keeps working without them.
          console.error("[CartDrawer] Add-on renderer failed to load:", error);
          this._addonsPromise = null;
          return null;
        });
    }
    return this._addonsPromise;
  }

  /**
   * Render add-ons for a context, loading the renderer if this is the first time.
   * @param {object|null|undefined} ctx
   */
  async _renderAddons(ctx) {
    if (!addonContextRenderable(ctx)) return;
    // Remember it even if the module is still in flight, so a morph or re-open can replay it.
    this._cacheRenderedContext(ctx);
    (await this._loadAddons())?.render(ctx);
  }

  /**
   * Repopulate add-ons after a reload or lazy hydrate. The decision is made against
   * sessionStorage before the renderer is touched, so the common page load never loads it.
   * @param {object|null|undefined} cartData
   */
  async _restoreAddons(cartData) {
    if (!cartData) return;
    if (this._addons) {
      this._cacheRenderedContext(this._addons.restore(cartData));
      return;
    }
    // Cheap probe before paying for the renderer. The renderer owns the actual decision — whether
    // the stored context belongs to this page and its anchor product is still in the cart.
    if (!hasRestorableAddonContext()) return;
    this._cacheRenderedContext((await this._loadAddons())?.restore(cartData));
  }

  /**
   * Every path that renders add-ons must land here, or closing the drawer hides them for good:
   * onClose() calls hide(), and the next open re-shows them only by replaying this context.
   * @param {object|null|undefined} ctx context that was actually rendered, or null
   */
  _cacheRenderedContext(ctx) {
    if (ctx) this._pendingAddonContext = ctx;
  }

  // ── Lazy hydration ───────────────────────────────────────────────

  /**
   * Fetch /cart.js on page load to populate header cart count badges.
   */
  async _fetchInitialCartCount() {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root}cart.js`, { credentials: 'same-origin' });
      if (!res.ok) return;
      const cartJson = await res.json();
      this._updateCartUI(cartJson);
      void this._restoreAddons(cartJson);
    } catch {
      // Non-critical — badge stays at 0 until next cart action
    }
  }

  /**
   * Hydrate the lazy drawer shell with full server-rendered HTML.
   * @returns {Promise<void>}
   */
  async _hydrateDrawer() {
    if (this._hydrated) return;
    if (this._hydratePromise) return this._hydratePromise;

    this._hydratePromise = (async () => {
      try {
        await this._refreshDrawerHTML();
        this._hydrated = true;
        this.element.removeAttribute('data-lazy-cart-drawer');
        this._initCartInstance();
      } catch (error) {
        console.error('[CartDrawer] Failed to hydrate lazy drawer:', error);
      } finally {
        this._hydratePromise = null;
      }
    })();

    return this._hydratePromise;
  }

  /**
   * Create the Cart class instance. Called once after DOM is ready.
   */
  _initCartInstance() {
    const itemsContainer = this.element.querySelector(CART_ITEMS_SEL);
    if (itemsContainer) this.cart = new Cart(itemsContainer, null);
  }

  /**
   * Show checkout CTA only when cart has at least one item.
   * @param {boolean} hasItems
   */
  _syncCheckoutVisibility(hasItems) {
    this.element.querySelectorAll(CHECKOUT_SLOT_SEL).forEach((slot) => {
      slot.classList.toggle("hidden", !hasItems);
      slot.setAttribute("aria-hidden", String(!hasItems));
    });
  }

  // ── Layout helpers ───────────────────────────────────────────────

  /**
   * Sticky vs static summary based on height ratio.
   */
  _updateStickyState() {
    const content = this.element.querySelector(SCROLL_AREA_SEL);
    const summary = this.element.querySelector(".cart-drawer__summary");

    if (!content || !summary) {
      this.element.setAttribute("data-cart-summary-sticky", "false");
      return;
    }

    const ratio = summary.getBoundingClientRect().height / this.element.getBoundingClientRect().height;
    this.element.setAttribute("data-cart-summary-sticky", ratio > this._summaryThreshold ? "false" : "true");
  }

  /**
   * Close drawer when Shopify installments CTA is clicked.
   */
  _setupInstallmentsClose() {
    if (typeof customElements === "undefined") return;

    customElements
      .whenDefined("shopify-payment-terms")
      .then(() => {
        const cta = document.querySelector("shopify-payment-terms")?.shadowRoot?.querySelector("#shopify-installments-cta");
        // Flag the element, not the instance: a morph can swap the CTA node out.
        if (!cta || cta.dataset.closeWired) return;
        cta.dataset.closeWired = "true";
        cta.addEventListener("click", () => this.close());
      })
      .catch(() => {});
  }

  // ── UI updates ───────────────────────────────────────────────────

  /**
   * Update cart count badges and subtotal.
   * @param {Object} cartData
   */
  _updateCartUI(cartData) {
    if (!cartData) return;

    const subtotalEl = this.element.querySelector("[data-cart-drawer-subtotal]");
    if (subtotalEl) subtotalEl.textContent = formatMoney(cartData.items_subtotal_price || 0);
    this._syncCheckoutVisibility((cartData.item_count || 0) > 0);

    for (const btn of document.querySelectorAll("[data-cart-button]")) {
      const count = btn.querySelector("[data-cart-count]");
      if (count) count.textContent = cartData.item_count || 0;
    }
  }

  /**
   * Ensure [data-cart-items] container exists (creates scaffold for lazy shells).
   * @returns {HTMLElement|null}
   */
  _ensureItemsContainer() {
    let itemsContainer = this.element.querySelector(CART_ITEMS_SEL);
    if (itemsContainer) return itemsContainer;

    const contentContainer = this.element.querySelector(ITEMS_CONTAINER_SEL);
    if (!contentContainer) return null;

    const scrollWrapper = document.createElement("div");
    scrollWrapper.className = "cart-drawer__content flex flex-1 flex-col overflow-y-auto";

    const innerWrapper = document.createElement("div");
    innerWrapper.className = "flex flex-col gap-4 px-4";

    itemsContainer = document.createElement("div");
    itemsContainer.className = "cart-drawer-items";
    itemsContainer.setAttribute("data-cart-items", "");

    innerWrapper.appendChild(itemsContainer);
    scrollWrapper.appendChild(innerWrapper);
    contentContainer.appendChild(scrollWrapper);

    this.element.setAttribute("data-has-items", "true");
    this._syncCheckoutVisibility(true);
    return itemsContainer;
  }

  // ── Optimistic UI ────────────────────────────────────────────────

  /**
   * Show a gray placeholder row for an add with no product data to preview
   * (e.g. add-on rows). Leaves already-rendered rows alone, same as
   * {@link _showOptimisticItem}.
   * @param {number} variantId
   */
  _showSkeleton(variantId) {
    const itemsContainer = this._revealItemsContainer();
    if (!itemsContainer) return;
    if (this._pulseExistingItem(itemsContainer, variantId)) return;

    const row = this._cloneItemTemplate("[data-cart-item-skeleton]");
    if (!row) return;
    row.dataset.itemId = String(variantId);
    itemsContainer.insertAdjacentElement("afterbegin", row);
  }

  /**
   * Show an optimistic preview of the item being added.
   * @param {Object} data - Optimistic data from AddToCart.#collectOptimisticData
   */
  _showOptimisticItem(data) {
    const itemsContainer = this._revealItemsContainer();
    if (!itemsContainer) return;

    if (!this._pulseExistingItem(itemsContainer, data.variantId)) {
      const row = this._buildOptimisticRow(data);
      if (row) itemsContainer.insertAdjacentElement("afterbegin", row);
    }
    this._optimisticallyUpdateTotals(data.price, data.quantity);
  }

  /**
   * Ensure the items container exists and is visible (an empty cart hides it).
   * @returns {HTMLElement|null}
   */
  _revealItemsContainer() {
    const itemsContainer = this._ensureItemsContainer();
    if (itemsContainer?.classList.contains("hidden")) {
      itemsContainer.classList.remove("hidden");
      this.element.setAttribute("data-has-items", "true");
      this._syncCheckoutVisibility(true);
    }
    return itemsContainer;
  }

  /**
   * If the variant is already a row, pulse it instead of adding a placeholder.
   * @returns {boolean} true when an existing row was found
   */
  _pulseExistingItem(itemsContainer, variantId) {
    const existingItem = itemsContainer.querySelector(`[data-item-id="${variantId}"]`);
    if (!existingItem) return false;
    existingItem.classList.add("animate-pulse");
    setTimeout(() => existingItem.classList.remove("animate-pulse"), FEEDBACK_RESET_MS);
    return true;
  }

  /**
   * Clone one of the row templates rendered by snippets/cart-drawer-item-templates.liquid.
   * Returns null when the template is absent — the server refresh lands shortly either way,
   * so a missing placeholder costs nothing but a plainer wait.
   * @param {string} selector
   * @returns {HTMLElement|null}
   */
  _cloneItemTemplate(selector) {
    const tpl = this.element.querySelector(selector);
    const row = tpl?.content?.firstElementChild?.cloneNode(true);
    if (!row) console.warn(`[CartDrawer] Missing row template ${selector}`);
    return row ?? null;
  }

  /**
   * Fill the optimistic row template with the product being added. Markup lives in Liquid
   * next to the real row it stands in for; this only sets text, URLs, and the image.
   * @param {Object} data
   * @returns {HTMLElement|null}
   */
  _buildOptimisticRow(data) {
    const row = this._cloneItemTemplate("[data-cart-item-optimistic]");
    if (!row) return null;

    row.dataset.itemId = String(data.variantId);
    if (data.productUrl) {
      // Only when known: `a.href = ""` resolves to the current document, leaving a focusable
      // link that silently reloads the page. No href at all is not focusable and not a link.
      row.querySelectorAll("[data-optimistic-link]").forEach((a) => {
        a.href = data.productUrl;
      });
    }

    const title = row.querySelector("[data-optimistic-title]");
    if (title) title.textContent = data.productTitle;

    const img = row.querySelector("[data-optimistic-image]");
    const imgPlaceholder = row.querySelector("[data-optimistic-image-placeholder]");
    if (img && data.imageUrl) {
      img.src = data.imageUrl;
      img.alt = data.productTitle;
      img.hidden = false;
      imgPlaceholder?.remove();
    }

    const price = row.querySelector("[data-optimistic-price]");
    if (price) price.textContent = formatMoney(data.price * data.quantity);

    const options = row.querySelector("[data-optimistic-options]");
    if (options && data.options.length && data.variantTitle !== "Default Title") {
      for (const opt of data.options) {
        const el = document.createElement("div");
        el.textContent = String(opt);
        options.appendChild(el);
      }
      options.hidden = false;
    }

    return row;
  }

  /**
   * Optimistically update subtotal and cart count badges.
   * @param {number} priceCents
   * @param {number} quantity
   */
  _optimisticallyUpdateTotals(priceCents, quantity) {
    const subtotalEl = this.element.querySelector("[data-cart-drawer-subtotal]");
    if (subtotalEl) {
      // Read the rendered text, not the in-memory cart JSON: the JSON is only refreshed
      // by the server round-trip, so back-to-back adds would each re-base off the same
      // stale total and drop the earlier bump. It also holds items_subtotal_price, while
      // this element renders cart.total_price (differs once a discount applies).
      // US-format assumption: digits and dot.
      const currentCents =
        Math.round(parseFloat(subtotalEl.textContent.replace(/[^0-9.]/g, "")) * 100) || 0;
      subtotalEl.textContent = formatMoney(currentCents + priceCents * quantity);
    }

    for (const btn of document.querySelectorAll("[data-cart-button]")) {
      const count = btn.querySelector("[data-cart-count]");
      if (count) count.textContent = (parseInt(count.textContent, 10) || 0) + quantity;
    }
  }

  /**
   * Remove an optimistic item from the drawer (used on add-to-cart failure).
   * @param {number} variantId
   */
  _removeOptimisticItem(variantId) {
    if (!variantId) return;
    this.element.querySelector(`[data-optimistic="true"][data-item-id="${variantId}"]`)?.remove();

    const itemsContainer = this.element.querySelector(CART_ITEMS_SEL);
    if (itemsContainer && !itemsContainer.querySelector("[data-cart-item]")) {
      itemsContainer.classList.add("hidden");
      this.element.setAttribute("data-has-items", "false");
      this._syncCheckoutVisibility(false);
    }
  }

  // ── Server refresh ───────────────────────────────────────────────

  /**
   * Refresh cart drawer HTML by fetching updated content from server.
   * Morphs [cart-items-container] while preserving the live addon section.
   *
   * @param {Object|null} cartData - Cart JSON from #syncCart (authoritative),
   *   or null for hydration / removal refreshes.
   * @param {boolean} isRetry - Internal: set on the single retry after a
   *   failed fetch, so a retry can't recurse.
   */
  async _refreshDrawerHTML(cartData = null, isRetry = false) {
    const gen = ++this._refreshGen;
    const contentContainer = this.element.querySelector(ITEMS_CONTAINER_SEL);
    const isAuthoritativeRefresh = cartData != null;
    let fetchFailed = false;
    // Set when this refresh bails as stale/superseded — it then owns no state release.
    let skipped = false;

    try {
      const url = `${window.Shopify?.routes?.root || "/"}cart?view=ajax-drawer&_=${Date.now()}`;
      const response = await fetch(url);
      if (!response.ok) {
        fetchFailed = true;
        return;
      }

      // Stale guard: skip if a newer refresh was queued, or if an add is
      // in-flight and this is a non-authoritative refresh (e.g. hydration).
      if (gen !== this._refreshGen || (this._addInFlight && !cartData)) {
        skipped = true;
        return;
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const newCartDrawer = doc.querySelector("[data-cart-drawer]");
      const newContentContainer = newCartDrawer?.querySelector(ITEMS_CONTAINER_SEL);

      // ── Preserve live addon section across morphs ──────────────
      // The server HTML always renders addons empty/hidden. If we already
      // have a populated addon section, detach it before morph and
      // reattach after. On first add (empty→populated) let the server HTML
      // provide the section, then trigger the initial addon fetch.
      // Null whenever the renderer was never loaded, which also means there is nothing to save.
      const liveAddonSection =
        this._addons?.detachForMorph(contentContainer, newContentContainer) ?? null;

      // ── Morph ─────────────────────────────────────────────────
      if (contentContainer && newContentContainer) {
        // Remove items mid-animation before morphing. Cart items have no `id`
        // so morph matches them positionally — it would reuse the removing
        // element's node for the next item, strip `data-removing`, and cause
        // the item to flash back into view before its content is replaced.
        for (const el of contentContainer.querySelectorAll('[data-removing]')) {
          el.remove();
        }
        morph(contentContainer, newContentContainer, MORPH_OPTIONS);
      } else if (!contentContainer && newContentContainer) {
        this.element.querySelector("form")?.appendChild(newContentContainer);
      }

      // ── Reattach or init addons ───────────────────────────────
      if (liveAddonSection) {
        this._addons.reattachAfterMorph(this.element.querySelector(SCROLL_AREA_SEL), liveAddonSection);
      } else if (addonContextRenderable(this._pendingAddonContext)) {
        void this._renderAddons(this._pendingAddonContext);
      }

      // ── Sync state flags ──────────────────────────────────────
      const newHasItems = newCartDrawer?.getAttribute("data-has-items");
      if (typeof newHasItems === "string") {
        this.element.setAttribute("data-has-items", newHasItems);
        this._syncCheckoutVisibility(newHasItems === "true");
      }

      // ── Rebind Cart instance ──────────────────────────────────
      const newItemsContainer = this.element.querySelector(CART_ITEMS_SEL);
      if (!this.cart && newItemsContainer) {
        this.cart = new Cart(newItemsContainer, null);
      } else if (this.cart && newItemsContainer) {
        this.cart.itemsContainer = newItemsContainer;
        this.cart.items.clear();
        this.cart.setupItemListeners();
      }

      // ── Sync cart data ────────────────────────────────────────
      if (cartData) {
        if (this.cart) this.cart.cart = cartData;
      } else {
        const cartRes = await fetch(`${window.Shopify?.routes?.root || "/"}cart.js`, {
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
        });
        if (cartRes.ok) {
          const freshData = await cartRes.json();
          if (this.cart) this.cart.cart = freshData;
          document.dispatchEvent(new CustomEvent(CART_EVENTS.updated, { detail: freshData }));
        }
      }

      void this._restoreAddons(cartData ?? this.cart?.cart ?? null);
    } catch (error) {
      fetchFailed = true;
      console.error("Failed to refresh cart drawer HTML:", error);
    } finally {
      // The mutation already succeeded server-side, so a failed refresh
      // leaves the DOM's data-line indexes stale — unlocking now would let
      // the next edit hit the wrong line. Retry once before unlocking; the
      // retry's own finally handles unlock either way.
      // ponytail: single retry, no backoff — add backoff if flaky networks show up in logs.
      if (fetchFailed && !isRetry && gen === this._refreshGen) {
        void this._refreshDrawerHTML(cartData, true);
      } else if (!skipped && gen === this._refreshGen) {
        // Only the newest authoritative refresh should release add-to-cart state.
        if (isAuthoritativeRefresh) this._addInFlight = false;

        // A quantity-change/remove request already succeeded server-side
        // before this refresh was dispatched (Cart.update() resolved first) —
        // a successful morph clears the loading state as a side effect, but
        // if this refresh threw, nothing else ever would. A skipped refresh
        // (hydration landing mid-add) must not unlock — the add's own refresh will.
        this.cart?._setAllItemsLoading(false);
      }
    }
  }
}

const init = () => {
  const cartDrawer = document.querySelector("[data-cart-drawer]");
  if (cartDrawer) new CartDrawer(cartDrawer).load();

  // Go-to-cart link loading state — shows spinner while navigating to /cart
  if (cartDrawer) {
    cartDrawer.addEventListener('click', (e) => {
      const link = e.target.closest('[data-go-to-cart-link]');
      if (link) link.setAttribute('data-go-to-cart-loading', '');
    });
  }
};
init();
